import React, {useEffect, useState} from 'react';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {ReactSortable} from 'react-sortablejs';
import {DashboardConfiguration, DashboardConfigurationStep} from '../types';
import {Modal} from './Modal';
import {WorkflowSearchFormFieldFilter} from './WorkflowSearchFormFieldFilter';
import ReactDatepicker from 'react-datepicker';

export type CustomizeDashboardProps = {
    workflowId: number;
    options: DashboardConfiguration;
    close: () => void;
    saveConfiguration: (options: DashboardConfiguration) => void;
}

export const CustomizeDashboardModal: React.FunctionComponent<CustomizeDashboardProps> = ({
                                                                                              workflowId,
                                                                                              options,
                                                                                              close,
                                                                                              saveConfiguration
                                                                                          }) => {
    const [selectedOptions, setSelectedOptions] = useState<DashboardConfiguration>({...options});
    const [searchFilters, setSearchFilters] = useState<any>([]);
    const [searchFiltersIsLoading, setSearchFiltersIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setSearchFiltersIsLoading(true);
        Flow.getInstance().getSearchFilters()
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSearchFilters(response.json.filters);

                    // if passed a workflow, update the form state
                    workflowId && response.json.filters.some((filter: any) => {
                        if (String(filter.id) === String(workflowId)) {
                            setSearchFilters({
                                ...searchFilters,
                                workflow: {
                                    ...filter,
                                    value: filter.id,
                                    label: filter.title
                                }
                            });
                            return true;
                        }
                        return false;
                    });
                }
                setSearchFiltersIsLoading(false);
            })
            .catch(() => {
                setSearchFiltersIsLoading(false);
            });
    }, [false]);

    console.log('FILTERS', searchFilters);

    return (
        <Modal size="lg">
            <div className="modal-header">
                <h5 className="modal-title">Customize Dashboard</h5>
                <button type="button"
                        className="close"
                        aria-label="Close"
                        tabIndex={-1}
                        onClick={close}
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
            <div className="modal-body">
                <h5>Options</h5>
                <p>Select whether to exclude weekends and non-business hours from duration calculations.</p>
                <div className="form-group pl-1">
                    <div className="form-check">
                        <input type="checkbox"
                               className="form-check-input"
                               id="exclude-weekends"
                               checked={selectedOptions.durationDisplay === 'business_days'}
                               onChange={(e) => {
                                   setSelectedOptions({
                                       ...selectedOptions,
                                       durationDisplay: e.target.checked ? 'business_days' : 'all_days'
                                   })
                               }}
                        />
                        <label className="form-check-label" htmlFor="exclude-weekends">
                            Exclude Weekends
                        </label>
                    </div>
                </div>
                <div className="form-group pl-1">
                    <div className="form-check">
                        <input type="checkbox"
                               className="form-check-input"
                               id="only-business-hours"
                               checked={selectedOptions.onlyBusinessHours}
                               onChange={(e) => {
                                   setSelectedOptions({
                                       ...selectedOptions,
                                       onlyBusinessHours: e.target.checked
                                   })
                               }}
                        />
                        <label className="form-check-label" htmlFor="only-business-hours">
                            Only Business Hours (8a - 5p)
                        </label>
                    </div>
                </div>
                <h5>Customize Steps</h5>
                <p>Select the steps that should be visible on the graphs. By dragging the steps, you can change the order in which they appear.</p>
                <div className="form-group">
                    <ReactSortable list={selectedOptions.steps}
                                   setList={(steps) => {
                                       setSelectedOptions({...selectedOptions, steps})
                                   }}
                                   handle=".gripper"
                                   dragoverBubble={true}
                    >
                        {selectedOptions.steps.map((step: DashboardConfigurationStep, index: number) => (
                            <div className="d-flex m-1 customize-dashboard-step"
                                 key={step.id}
                            >
                                <div className="gripper pt-2 pb-2 pl-3 pr-3 mr-3">
                                    <i className="fas fa-arrows-alt-v"/>
                                </div>
                                <div className="form-check p-2">
                                    <input type="checkbox"
                                           className="form-check-input"
                                           id={`step-${step.id}`}
                                           checked={step?.checked || false}
                                           onChange={(e) => {

                                               setSelectedOptions({
                                                   ...selectedOptions,
                                                   steps: [
                                                       ...selectedOptions.steps.slice(0, index),
                                                       {
                                                           ...step,
                                                           checked: e.target.checked
                                                       },
                                                       ...selectedOptions.steps.slice(index + 1),
                                                   ]
                                               })
                                           }}
                                    />
                                    <label className="form-check-label" htmlFor={`step-${step.id}`}>
                                        {step.name}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </ReactSortable>
                </div>
                <h5>Filter</h5>
                <p>Apply filters to the data</p>
                {console.log('selectedOptions', selectedOptions)}
                <div className="d-flex align-items-center">
                    <label className="pr-1">Start Date</label>
                    <ReactDatepicker
                        className="form-control"
                        selected={selectedOptions.startDate || null}
                        onChange={(selected) => {
                            console.log('setting startDate', selected);
                            setSelectedOptions({
                                ...selectedOptions,
                                startDate: selected,
                                startDateText: selected?.toISOString().slice(0, 10) || ''
                            });
                        }}
                    />
                    <label className="ml-3 pr-1">End Date</label>
                    <ReactDatepicker
                        className="form-control"
                        selected={selectedOptions.endDate || null}
                        onChange={(selected) => {
                            setSelectedOptions({
                                ...selectedOptions,
                                endDate: selected,
                                endDateText: selected?.toISOString().slice(0, 10) || ''
                            });
                        }}
                    />

                </div>

                {searchFiltersIsLoading && <></>}
                <div className="form-group" id="customize-dashboard-filters">
                    {selectedOptions?.fieldFilters?.map((fieldFilter: any, index: number) => (
                        <WorkflowSearchFormFieldFilter
                            key={index}
                            // @ts-ignore
                            filters={searchFilters?.workflow?.filters}
                            operators={fieldFilter?.workflowField?.operators}
                            values={fieldFilter?.workflowField?.values}
                            filterValues={fieldFilter}
                            onChange={(field: string, value: any) => {
                                setSelectedOptions({
                                    ...selectedOptions,
                                    fieldFilters: [
                                        ...selectedOptions.fieldFilters.slice(0, index),
                                        {
                                            ...selectedOptions.fieldFilters[index],
                                            [field]: value
                                        },
                                        ...selectedOptions.fieldFilters.slice(index + 1)
                                    ],
                                });
                            }}
                        />
                    ))}
                    <button
                        type="button"
                        className="btn btn-light text-muted mt-2"
                        onClick={() => {
                            setSelectedOptions({
                                ...selectedOptions,
                                fieldFilters: [
                                    ...selectedOptions.fieldFilters,
                                    {}
                                ]
                            })
                        }}
                    >
                        <i className="fas fa-plus"/>
                        <span className="pl-3">Add Filter</span>
                    </button>
                </div>
            </div>
            <div className="modal-footer">
                <button type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            saveConfiguration(selectedOptions);
                        }}
                >Save
                </button>
            </div>
        </Modal>
    );
};